/**
 * A chat line, with its links made clickable.
 *
 * Josh, 2026-09-07: "can the link be actually clickable and stay within the
 * lines?" Connor hands over PDF links now, and a 140-character URL rendered as
 * plain text is both unusable and wider than the bubble.
 *
 * ⚠️ BUILT AS REACT NODES, NEVER AS HTML. The obvious way to linkify is to
 * replace matches with <a> and set innerHTML, and this text comes from a model
 * that has just been reading the open web — the one input in this product most
 * likely to contain markup somebody else wrote. Splitting the string and
 * rendering an <a> element for each match means nothing in the text can ever be
 * interpreted as markup at all.
 *
 * ⚠️ TRAILING PUNCTUATION IS NOT PART OF THE LINK. "…see the PDF at https://x
 * .com/form.pdf." would otherwise produce a URL ending in a full stop, which
 * 404s — and the member blames the link, not the sentence it was in.
 */
import { Fragment } from 'react';

/** http/https only. A bare www. match would need a scheme invented for it. */
const URL_RE = /(https?:\/\/[^\s<>()]+)/g;

/** Punctuation a sentence puts after a URL, which is never part of it. */
const TRAILING = /[.,;:!?'"）】]+$/;

export function ChatText({ text }: { text: string }): JSX.Element {
  const parts = text.split(URL_RE);
  return (
    <>
      {parts.map((part, i) => {
        // split() with one capture group puts the matches at the odd indexes.
        if (i % 2 === 0) return <Fragment key={i}>{part}</Fragment>;

        const trail = TRAILING.exec(part)?.[0] ?? '';
        const href = trail ? part.slice(0, -trail.length) : part;
        return (
          <Fragment key={i}>
            <a
              className="chat-link"
              href={href}
              target="_blank"
              /* noreferrer as well as noopener: the destination is a page a
                 model found, and it has no business knowing where it came
                 from. */
              rel="noopener noreferrer"
            >
              {href}
            </a>
            {trail}
          </Fragment>
        );
      })}
    </>
  );
}
